import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, TextField, IconButton, Button, Alert, CircularProgress, Avatar } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import MicIcon from "@mui/icons-material/Mic";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { useTranslation } from "react-i18next";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { db } from "../firebaseConfig";
import {
  sendExerciseAiMessage,
  escalateExercise,
  submitHandwrittenAnswer,
  resubmitAnswerWithCorrection,
  transcribeVoice,
} from "../services/exerciseAiService";
import { prepareSubmissionFile } from "../utils/submissionImage";
import ChatMarkdown from "./ChatMarkdown";
import ImageLightbox from "./ImageLightbox";
import CameraCapture from "./CameraCapture";

const MAX_SUBMISSION_PHOTOS = 6;

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// The transcription step wraps anything it wasn't confident reading
// in ⟨ ⟩ — surfaced here as a highlighted span so the student can see
// exactly which part of the reading to double-check, instead of
// trusting (or distrusting) the whole transcript uniformly.
const renderTranscriptWithHighlights = (transcript) =>
  String(transcript || "")
    .split(/(⟨[^⟩]*⟩)/g)
    .map((part, i) =>
      part.startsWith("⟨") && part.endsWith("⟩") ? (
        <Box
          key={i}
          component="mark"
          sx={{ bgcolor: "#fef08a", color: "#78350f", px: 0.25, borderRadius: 0.5 }}
        >
          {part.slice(1, -1)}
        </Box>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      )
    );

const ROLE_STYLES = {
  student: { side: "end", bg: "#e9eefb", color: "#1f2937", avatar: false, label: "" },
  assistant: { side: "start", bg: "#f3f4f6", color: "#1f2937", avatar: true, label: "Darssy AI" },
  teacher: { side: "start", bg: "#e6f6ec", color: "#1f2937", avatar: false, label: "" },
};

const formatTime = (value) => {
  try {
    const date =
      value?.toDate?.() ||
      (typeof value?.seconds === "number" ? new Date(value.seconds * 1000) : null) ||
      (value instanceof Date ? value : null);
    if (!date || Number.isNaN(date.getTime())) return "";
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
};

const CHAT_TEXT_SX = {
  fontSize: 12.5,
  lineHeight: 1.5,
  "& .MuiTypography-root": { fontSize: "inherit", lineHeight: "inherit" },
};

export default function StudentExerciseAI({ groupId, exerciseId, exercise }) {
  const { t, i18n } = useTranslation();
  const dir = i18n.dir();
  const uid = getAuth().currentUser?.uid;
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [pendingMessage, setPendingMessage] = useState("");
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);
  const [error, setError] = useState("");
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [stagedPhotos, setStagedPhotos] = useState([]);
  const [correctingMessage, setCorrectingMessage] = useState(null);
  const [isResubmittingCorrection, setIsResubmittingCorrection] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceDraft, setVoiceDraft] = useState(null);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const recorderRef = useRef(null);
  const recordStreamRef = useRef(null);
  const recordChunksRef = useRef([]);

  useEffect(() => {
    if (!groupId || !exerciseId || !uid) return undefined;

    const conversationRef = doc(db, "groups", groupId, "exercises", exerciseId, "conversations", uid);
    const unsubConversation = onSnapshot(conversationRef, (snap) => {
      setConversation(snap.exists() ? snap.data() : null);
    });

    const messagesRef = query(collection(conversationRef, "messages"), orderBy("createdAt", "asc"));
    const unsubMessages = onSnapshot(messagesRef, (snapshot) => {
      setMessages(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubConversation();
      unsubMessages();
    };
  }, [groupId, exerciseId, uid]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isSending) return;
    setError("");
    const message = input.trim();
    setInput("");
    setPendingMessage(message);
    setIsSending(true);

    try {
      await sendExerciseAiMessage({ groupId, exerciseId, message });
    } catch (err) {
      setError(err.message || t("تعذّر إرسال الرسالة."));
    } finally {
      setIsSending(false);
      setPendingMessage("");
    }
  };

  const addStagedPhoto = (file) => {
    if (!file) return;
    setStagedPhotos((prev) => {
      if (prev.length >= MAX_SUBMISSION_PHOTOS) return prev;
      return [
        ...prev,
        {
          id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
          file,
          previewUrl: URL.createObjectURL(file),
          mimeType: file.type || "image/jpeg",
        },
      ];
    });
  };

  const removeStagedPhoto = (id) => {
    setStagedPhotos((prev) => {
      const removed = prev.find((p) => p.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const clearStagedPhotos = () => {
    setStagedPhotos((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
  };

  const sendStagedPhotos = async () => {
    if (!stagedPhotos.length || isSubmittingAnswer) return;

    setError("");
    setIsSubmittingAnswer(true);
    try {
      const images = await Promise.all(
        stagedPhotos.map(async (photo) => ({
          base64: await readFileAsBase64(photo.file),
          mimeType: photo.mimeType,
        }))
      );
      await submitHandwrittenAnswer({ groupId, exerciseId, images });
      clearStagedPhotos();
    } catch (err) {
      setError(err.message || t("تعذّر إرسال الإجابة للتقييم."));
    } finally {
      setIsSubmittingAnswer(false);
    }
  };

  const handlePhotoSelected = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    for (const file of files) {
      try {
        addStagedPhoto(await prepareSubmissionFile(file));
      } catch {
        setError(t("تعذّرت قراءة إحدى الصور المختارة، جرّب صورة أخرى."));
      }
    }
  };

  const handleCameraCapture = (blob) => {
    addStagedPhoto(blob);
  };

  const stopRecordingStream = () => {
    recordStreamRef.current?.getTracks().forEach((track) => track.stop());
    recordStreamRef.current = null;
  };

  const startRecording = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      recordChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data?.size > 0) recordChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        stopRecordingStream();
        setIsRecording(false);
        const mimeType = recorder.mimeType || "audio/webm";
        const blob = new Blob(recordChunksRef.current, { type: mimeType });
        recordChunksRef.current = [];
        if (!blob.size) return;

        setIsTranscribing(true);
        try {
          const audioBase64 = await readFileAsBase64(blob);
          const result = await transcribeVoice({ groupId, audioBase64, audioMimeType: mimeType });
          setVoiceDraft({ text: result.transcript });
        } catch (err) {
          setError(err.message || t("تعذّرت قراءة التسجيل."));
        } finally {
          setIsTranscribing(false);
        }
      };

      recorder.start();
      setIsRecording(true);
    } catch {
      setError(t("تعذّر الوصول إلى الميكروفون."));
      stopRecordingStream();
    }
  };

  const stopRecording = () => recorderRef.current?.stop();

  const sendVoiceDraft = async () => {
    const text = voiceDraft?.text.trim();
    if (!text || isSending) return;
    setError("");
    setVoiceDraft(null);
    setPendingMessage(text);
    setIsSending(true);
    try {
      await sendExerciseAiMessage({ groupId, exerciseId, message: text });
    } catch (err) {
      setError(err.message || t("تعذّر إرسال الرسالة."));
    } finally {
      setIsSending(false);
      setPendingMessage("");
    }
  };

  const handleStartCorrection = (message) => {
    setError("");
    setCorrectingMessage({
      messageId: message.id,
      submissionMessageId: message.submissionMessageId,
      text: message.transcript || "",
    });
  };

  const handleResubmitCorrection = async () => {
    if (!correctingMessage || isResubmittingCorrection) return;
    if (!correctingMessage.text.trim()) return;

    setError("");
    setIsResubmittingCorrection(true);
    try {
      await resubmitAnswerWithCorrection({
        groupId,
        exerciseId,
        submissionMessageId: correctingMessage.submissionMessageId,
        correctedTranscript: correctingMessage.text.trim(),
      });
      setCorrectingMessage(null);
    } catch (err) {
      setError(err.message || t("تعذّر إعادة إرسال التصحيح."));
    } finally {
      setIsResubmittingCorrection(false);
    }
  };

  const handleEscalate = async () => {
    setError("");
    try {
      await escalateExercise({ groupId, exerciseId });
    } catch (err) {
      setError(err.message || t("تعذّر تحويل الطلب إلى الأستاذ."));
    }
  };

  if (!exercise?.aiEnabled) {
    return (
      <Typography sx={{ color: "#94a3b8", textAlign: "center", mt: 4 }}>
        {t("Darssy AI غير مفعّل لهذا التمرين.")}
      </Typography>
    );
  }

  if (exercise?.reviewStatus !== "validated") {
    return (
      <Typography sx={{ color: "#94a3b8", textAlign: "center", mt: 4 }}>
        {t("لم يراجع الأستاذ هذا التمرين بعد، Darssy AI غير متاح حالياً.")}
      </Typography>
    );
  }

  const renderRow = (message, { opacity, typing } = {}) => {
    const { id: key, role, content, createdAt, transcript, submissionMessageId } = message;
    const rs = ROLE_STYLES[role] || ROLE_STYLES.student;
    const isEnd = rs.side === "end";
    const time = typing ? "" : formatTime(createdAt);
    const imageUrls = Array.isArray(message.imageUrls)
      ? message.imageUrls
      : message.imageUrl
      ? [message.imageUrl]
      : [];
    const canFlagReading = !typing && message.type === "evaluation" && submissionMessageId;
    const isCorrectingThis = correctingMessage?.messageId === key;

    return (
      <Box
        key={key}
        sx={{
          display: "flex",
          direction: "ltr",
          justifyContent: isEnd ? "flex-end" : "flex-start",
          gap: 0.75,
          opacity: opacity || 1,
        }}
      >
        {rs.avatar && (
          <Avatar
            sx={{
              width: 24,
              height: 24,
              mt: 0.25,
              fontSize: 12,
              fontWeight: 700,
              bgcolor: "#2f6fed",
              color: "#fff",
              flexShrink: 0,
            }}
          >
            <Box component="span" sx={{ fontSize: 10 }}>AI</Box>
          </Avatar>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: isEnd ? "flex-end" : "flex-start",
            maxWidth: "84%",
          }}
        >
          <Box sx={{ bgcolor: rs.bg, color: rs.color, borderRadius: 2.5, px: 1.25, py: 0.85 }}>
            {imageUrls.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
                {imageUrls.map((url, i) => (
                  <Box
                    key={i}
                    component="img"
                    src={url}
                    alt=""
                    onClick={() => setLightboxUrl(url)}
                    sx={{
                      maxWidth: imageUrls.length > 1 ? 110 : 180,
                      maxHeight: imageUrls.length > 1 ? 110 : 180,
                      borderRadius: 1.5,
                      display: "block",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </Box>
            )}
            {typing ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <CircularProgress size={12} sx={{ color: "#64748b" }} />
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                  {t("Darssy AI يكتب الآن...")}
                </Typography>
              </Box>
            ) : (
              <Box dir="auto">
                <ChatMarkdown content={content} sx={CHAT_TEXT_SX} />
              </Box>
            )}
            {transcript && !isCorrectingThis && (
              <Box
                dir="auto"
                sx={{
                  mt: 0.75,
                  pt: 0.75,
                  borderTop: "1px dashed #d1d5db",
                  fontSize: 11.5,
                  lineHeight: 1.5,
                  color: "#4b5563",
                }}
              >
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#7c3aed", mb: 0.25 }}>
                  {t("النص الذي قرأته من ورقتك — تحقّق منه:")}
                </Typography>
                {renderTranscriptWithHighlights(transcript)}
                {message.transcriptReliable === false && (
                  <Typography sx={{ fontSize: 10.5, color: "#b45309", mt: 0.5 }}>
                    {t("تعذّرت قراءة جزء من الورقة، فقد يكون هذا النص ناقصاً.")}
                  </Typography>
                )}
                {message.lowResolution && message.transcriptReliable !== false && (
                  <Typography sx={{ fontSize: 10.5, color: "#b45309", mt: 0.5 }}>
                    {t("الصورة صغيرة نسبياً، لذلك راجع هذا النص بعناية أكبر.")}
                  </Typography>
                )}
              </Box>
            )}
            {!transcript && message.transcriptReliable === false && !isCorrectingThis && (
              <Box
                dir="auto"
                sx={{
                  mt: 0.75,
                  pt: 0.75,
                  borderTop: "1px dashed #d1d5db",
                  fontSize: 11.5,
                  lineHeight: 1.5,
                  color: "#b45309",
                }}
              >
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, mb: 0.25 }}>
                  {t("لم أتمكن من قراءة ما كتبته")}
                </Typography>
                {t(
                  "الصورة غير كافية لقراءة خطك بثقة، لذلك لا تعتمد كثيراً على " +
                  "هذا التقييم. أعد التقاط الصورة من مسافة أقرب، بإضاءة جيدة " +
                  "وبدون ظل على الورقة."
                )}
              </Box>
            )}
            {isCorrectingThis && (
              <Box sx={{ mt: 0.75, pt: 0.75, borderTop: "1px dashed #d1d5db" }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  dir="auto"
                  value={correctingMessage.text}
                  onChange={(e) =>
                    setCorrectingMessage((prev) => ({ ...prev, text: e.target.value }))
                  }
                  sx={{ bgcolor: "#fff", borderRadius: 1 }}
                />
                <Box sx={{ display: "flex", gap: 0.75, mt: 0.75 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleResubmitCorrection}
                    disabled={isResubmittingCorrection}
                    sx={{ textTransform: "none", fontSize: 11.5, bgcolor: "#7c3aed", "&:hover": { bgcolor: "#6d28d9" } }}
                  >
                    {isResubmittingCorrection ? t("جارٍ الإرسال...") : t("إعادة التقييم بهذا التصحيح")}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setCorrectingMessage(null)}
                    disabled={isResubmittingCorrection}
                    sx={{ textTransform: "none", fontSize: 11.5, color: "#64748b" }}
                  >
                    {t("إلغاء")}
                  </Button>
                </Box>
              </Box>
            )}
            {canFlagReading && !isCorrectingThis && (
              <Box sx={{ mt: 0.75, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  onClick={() => handleStartCorrection(message)}
                  sx={{ textTransform: "none", fontSize: 10.5, color: "#7c3aed", p: 0, minWidth: 0 }}
                >
                  {t("لم أكتب هذا؟ صحّح النص")}
                </Button>
                <Button
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ textTransform: "none", fontSize: 10.5, color: "#64748b", p: 0, minWidth: 0 }}
                >
                  {t("أعد رفع الصور")}
                </Button>
              </Box>
            )}
          </Box>
          {time && (
            <Typography sx={{ fontSize: 10, color: "#9aa4b2", mt: 0.25 }}>{time}</Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", direction: dir, bgcolor: "#fff" }}>
      {conversation?.escalated && (
        <Alert severity="info" sx={{ mb: 1, fontSize: 12 }}>
          {t("تم إشعار الأستاذ، سيرد عليك قريباً.")}
        </Alert>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          px: 1.25,
          py: 1.25,
        }}
      >
        {messages.length === 0 && (
          <Typography sx={{ color: "#94a3b8", textAlign: "center", mt: 2, fontSize: 12.5 }}>
            {t("اطرح سؤالاً حول هذا التمرين لتبدأ المحادثة مع Darssy AI.")}
          </Typography>
        )}

        {messages.map((message) => renderRow(message))}

        {isSending && pendingMessage &&
          renderRow(
            { id: "pending", role: "student", content: pendingMessage },
            { opacity: 0.7 }
          )}

        {isSending &&
          renderRow({ id: "typing", role: "assistant" }, { typing: true })}

        <div ref={bottomRef} />
      </Box>

      {error && (
        <Typography sx={{ color: "red", textAlign: "center", mb: 1, fontSize: 12.5 }}>{error}</Typography>
      )}

      {isSubmittingAnswer && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
          <CircularProgress size={16} />
          <Typography sx={{ fontSize: 13, color: "#7c3aed" }}>
            {t("جارٍ تقييم إجابتك...")}
          </Typography>
        </Box>
      )}

      {(isTranscribing || voiceDraft) && (
        <Box sx={{ px: 1.25, pb: 1 }}>
          <Box sx={{ border: "1px solid #e6e8ef", borderRadius: 1.5, p: 1.25, bgcolor: "#faf9ff" }}>
            {isTranscribing ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
                  {t("جارٍ تحويل تسجيلك إلى نص...")}
                </Typography>
              </Box>
            ) : (
              <>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#7c3aed", mb: 0.5 }}>
                  {t("ما سمعته منك — راجعه قبل الإرسال:")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  dir="auto"
                  value={voiceDraft.text}
                  onChange={(e) => setVoiceDraft({ text: e.target.value })}
                  sx={{ bgcolor: "#fff", borderRadius: 1 }}
                />
                <Box sx={{ display: "flex", gap: 0.75, mt: 1, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={sendVoiceDraft}
                    disabled={isSending || !voiceDraft.text.trim()}
                    sx={{ textTransform: "none", fontSize: 11.5, bgcolor: "#7c3aed", "&:hover": { bgcolor: "#6d28d9" } }}
                  >
                    {t("إرسال")}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setVoiceDraft(null)}
                    sx={{ textTransform: "none", fontSize: 11.5, color: "#64748b" }}
                  >
                    {t("إلغاء")}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {stagedPhotos.length > 0 && (
        <Box sx={{ px: 1.25, pb: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {stagedPhotos.map((photo) => (
              <Box key={photo.id} sx={{ position: "relative" }}>
                <Box
                  component="img"
                  src={photo.previewUrl}
                  alt=""
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: "cover",
                    borderRadius: 1,
                    border: "1px solid #e6e8ef",
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => removeStagedPhoto(photo.id)}
                  disabled={isSubmittingAnswer}
                  sx={{
                    position: "absolute",
                    top: -8,
                    insetInlineEnd: -8,
                    width: 20,
                    height: 20,
                    bgcolor: "#fff",
                    boxShadow: 1,
                    "&:hover": { bgcolor: "#f3f4f6" },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontSize: 11.5, color: "#94a3b8" }}>
              {t("{{count}} صورة جاهزة للإرسال (الحد الأقصى {{max}})", {
                count: stagedPhotos.length,
                max: MAX_SUBMISSION_PHOTOS,
              })}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={sendStagedPhotos}
              disabled={isSubmittingAnswer}
              sx={{ textTransform: "none", fontSize: 12, bgcolor: "#7c3aed", "&:hover": { bgcolor: "#6d28d9" } }}
            >
              {t("إرسال للتقييم")}
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ px: 1.25, pb: 1.25, pt: 0.5 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handlePhotoSelected}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            border: "1px solid #e6e8ef",
            borderRadius: 1,
            bgcolor: "#fff",
            px: 0.5,
            py: 0.25,
          }}
        >
          <IconButton
            size="small"
            title={isRecording ? t("أوقف التسجيل") : t("أجب بصوتك")}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={isSubmittingAnswer || isTranscribing || stagedPhotos.length > 0}
            sx={{ color: isRecording ? "#dc2626" : "#64748b", flexShrink: 0 }}
          >
            {isRecording ? <StopCircleIcon sx={{ fontSize: 20 }} /> : <MicIcon sx={{ fontSize: 18 }} />}
          </IconButton>
          <IconButton
            size="small"
            title={t("التقط صورة لإجابتك المكتوبة بخط اليد")}
            onClick={() => setIsCameraOpen(true)}
            disabled={isSubmittingAnswer || Boolean(voiceDraft) || isRecording || stagedPhotos.length >= MAX_SUBMISSION_PHOTOS}
            sx={{ color: "#64748b", flexShrink: 0 }}
          >
            <CameraAltIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            title={t("أرسل صورة إجابتك من الملفات")}
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmittingAnswer || Boolean(voiceDraft) || isRecording || stagedPhotos.length >= MAX_SUBMISSION_PHOTOS}
            sx={{ color: "#64748b", flexShrink: 0 }}
          >
            <AttachFileIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <TextField
            fullWidth
            variant="standard"
            placeholder={t("اكتب سؤالك هنا...")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            disabled={isSending}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13 } }}
            sx={{ mx: 0.5 }}
          />
          <IconButton
            onClick={handleSend}
            disabled={isSending}
            sx={{
              flexShrink: 0,
              width: 32,
              height: 32,
              bgcolor: "#2f6fed",
              color: "#fff",
              "&:hover": { bgcolor: "#255ac9" },
              "&.Mui-disabled": { bgcolor: "#c7d6f6", color: "#fff" },
            }}
          >
            <SendIcon sx={{ fontSize: 18, transform: dir === "rtl" ? "scaleX(-1)" : "none" }} />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          borderTop: "1px solid #eef1f6",
          px: 1.25,
          py: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: 11.5,
            color: "#94a3b8",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <SupportAgentIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          <Box
            component="span"
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {t("تحتاج مزيداً من المساعدة؟ حوِّل الطلب إلى أستاذك.")}
          </Box>
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<SupportAgentIcon />}
          onClick={handleEscalate}
          disabled={conversation?.escalated}
          sx={{
            flexShrink: 0,
            textTransform: "none",
            borderRadius: 1,
            borderColor: "#c9d8f5",
            color: "#2f6fed",
            fontSize: 12,
            fontWeight: 600,
            "&:hover": { borderColor: "#2f6fed", bgcolor: "#f4f8ff" },
          }}
        >
          {conversation?.escalated ? t("تم طلب الأستاذ") : t("اطلب مساعدة الأستاذ")}
        </Button>
      </Box>

      <ImageLightbox src={lightboxUrl} onClose={() => setLightboxUrl(null)} />
      <CameraCapture
        open={isCameraOpen}
        onClose={() => setIsCameraOpen(false)}
        onCapture={handleCameraCapture}
        onFallbackToFile={() => {
          setIsCameraOpen(false);
          fileInputRef.current?.click();
        }}
      />
    </Box>
  );
}
